import React from 'react';
import type { Dayjs } from 'dayjs';
import { AppointmentType, DefaultCustomField, MessageTemplate } from 'src/constants/types';
import { FormMessage } from '../../types';
import type { MessageTemplate as ConfigMessageTemplate } from '../../types';
import { MessageTextSection } from './MessageTextSection';
import { DataCaptureSection } from './DataCaptureSection';
import { SchedulingSection } from './SchedulingSection';
import { ScheduleKind } from '../../types';

interface MessageFormProps {
  message: FormMessage;
  onChange: (next: Partial<FormMessage>) => void;
  scheduleKind: ScheduleKind;
  position: number;                       // 1-based
  allowedMessageTypes: string[];          // from policy/config (e.g. ['sms','mms'])
  /** Config template governing this message; drives field visibility. */
  configMessageTemplate?: ConfigMessageTemplate;
  metadata: DefaultCustomField[];         // for TemplateText customFields + capture select
  messageTemplates?: MessageTemplate[];   // for TemplateText
  optOutText?: string;                    // for MessageCreditEstimates pos1 suffix
  surveyEnabled?: boolean;
  captureEnabled?: boolean;
  disabled?: boolean;
  /** Earliest time this message's picker may offer — a minute past the one before. */
  minDateTime?: Dayjs | null;
  /** Why this message is out of order, shown under its time picker. */
  scheduleTimeError?: string;
  /** Calendars available for per-message appointment attachment. */
  appointmentTypes?: AppointmentType[];
  appointmentsLoading?: boolean;
  onRequestRefreshAppointments?: () => void;
  isFirstEnrollmentScheduledMessage?: boolean;
  onValidationChange?: (messageId: string, isValid: boolean) => void;
  companyId?: string;
}

export const MessageForm: React.FC<MessageFormProps> = ({
  message,
  onChange,
  scheduleKind,
  position,
  allowedMessageTypes,
  configMessageTemplate,
  metadata,
  messageTemplates = [],
  optOutText,
  surveyEnabled,
  captureEnabled,
  disabled,
  minDateTime,
  scheduleTimeError,
  appointmentTypes,
  appointmentsLoading,
  onRequestRefreshAppointments,
  isFirstEnrollmentScheduledMessage,
  onValidationChange,
  companyId,
}) => {
  const isReminder = scheduleKind === 'reminder';
  const isAnnual = scheduleKind === 'annual';
  const isFlyer = scheduleKind === 'flyer';
  const isJoin = scheduleKind === 'join' || scheduleKind === 'enrollment';

  // Config-template visibility flags. With no template scheduling and capture show; with
  // one, the template decides. The survey checkbox
  // additionally stays visible for a message that is already a survey, so an existing one is
  // never silently hidden.
  const effectiveScheduleVisible = configMessageTemplate
    ? !!configMessageTemplate.scheduleVisible
    : true;
  const effectiveDataCaptureVisible = configMessageTemplate
    ? !!configMessageTemplate.dataCaptureVisible
    : true;
  const effectiveSurveyVisible = configMessageTemplate
    ? !!message.isSurvey || !!configMessageTemplate.surveyVisible
    : !!message.isSurvey;

  const handleValidationChange = (isValid: boolean) => {
    const key = message.clientKey || `pos-${position}`;
    onValidationChange?.(key, isValid);
  };

  return (
    <>
      <MessageTextSection
        message={message}
        onChange={onChange}
        allowedMessageTypes={allowedMessageTypes}
        configMessageTemplate={configMessageTemplate}
        metadata={metadata}
        messageTemplates={messageTemplates}
        position={position}
        optOutText={optOutText}
        isReminder={isReminder}
        isFlyer={isFlyer}
        disabled={disabled}
        appointmentTypes={appointmentTypes}
        appointmentsLoading={appointmentsLoading}
        onRequestRefreshAppointments={onRequestRefreshAppointments}
        onValidationChange={handleValidationChange}
        companyId={companyId}
      />
      <DataCaptureSection
        message={message}
        onChange={onChange}
        metadata={metadata}
        surveyEnabled={surveyEnabled !== false && effectiveSurveyVisible}
        captureEnabled={captureEnabled !== false && effectiveDataCaptureVisible}
        isReminder={isReminder}
        isAnnual={isAnnual}
        isJoin={isJoin}
        disabled={disabled}
        companyId={companyId}
      />
      {/* Every scheduling control is hidden when the template says so. */}
      {effectiveScheduleVisible && (
      <SchedulingSection
        message={message}
        onChange={onChange}
        scheduleKind={scheduleKind}
        position={position}
        disabled={disabled}
        minDateTime={minDateTime}
        scheduleTimeError={scheduleTimeError}
        isFirstEnrollmentScheduledMessage={isFirstEnrollmentScheduledMessage}
      />
      )}
    </>
  );
};
